import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';

export interface AISearchAction {
    action: 'applyQuery' | 'clearQuery';
    query?: string;
}

export interface OpenAIResponse {
    choices: Array<{
        message: {
            content: string;
        };
    }>;
}

export interface ColumnMapping {
    originalField: string;
    displayName: string;
    dummyName: string;
    dataType: string;
}

@Injectable()
export class AISearchPanelService {
    private actionSubject = new Subject<AISearchAction>();
    public action$ = this.actionSubject.asObservable();

    private columnMappingsSubject = new BehaviorSubject<ColumnMapping[]>([]);
    public columnMappings$ = this.columnMappingsSubject.asObservable();

    private appliedQuerySubject = new BehaviorSubject<string | null>(null);
    public appliedQuery$ = this.appliedQuerySubject.asObservable();

    private loadingSubject = new BehaviorSubject<boolean>(false);
    public loading$ = this.loadingSubject.asObservable();

    private errorSubject = new BehaviorSubject<string | null>(null);
    public error$ = this.errorSubject.asObservable();

    // OpenAI API configuration - you'll need to set your API key
    private readonly OPENAI_API_URL = 'https://api.openai.com/v1/chat/completions';
    private readonly OPENAI_API_KEY = ''; // TODO: Set your actual API key
    private dummyNames = [
        // Fruits (A-Z)
        'Apple',
        'Apricot',
        'Avocado',
        'Banana',
        'Blackberry',
        'Blueberry',
        'Cherry',
        'Coconut',
        'Cranberry',
        'Date',
        'Dragonfruit',
        'Elderberry',
        'Fig',
        'Grape',
        'Grapefruit',
        'Guava',
        'Honeydew',
        'Kiwi',
        'Lemon',
        'Lime',
        'Lychee',
        'Mango',
        'Melon',
        'Nectarine',
        'Orange',
        'Papaya',
        'Peach',
        'Pear',
        'Pineapple',
        'Plum',
        'Pomegranate',
        'Quince',
        'Raspberry',
        'Strawberry',
        'Tangerine',
        'Ugli',
        'Vanilla',
        'Watermelon',
        'Xigua',
        'Yuzu',
        'Zucchini',

        // Animals (A-Z)
        'Antelope',
        'Bear',
        'Cheetah',
        'Dolphin',
        'Elephant',
        'Flamingo',
        'Giraffe',
        'Hippo',
        'Iguana',
        'Jaguar',
        'Kangaroo',
        'Lion',
        'Monkey',
        'Narwhal',
        'Octopus',
        'Penguin',
        'Quail',
        'Rabbit',
        'Sloth',
        'Tiger',
        'Unicorn',
        'Vulture',
        'Walrus',
        'Xerus',
        'Yak',
        'Zebra',

        // Colors (A-Z)
        'Amber',
        'Blue',
        'Crimson',
        'Dandelion',
        'Emerald',
        'Fuchsia',
        'Gold',
        'Honey',
        'Ivory',
        'Jade',
        'Khaki',
        'Lavender',
        'Magenta',
        'Navy',
        'Orange',
        'Purple',
        'Quartz',
        'Ruby',
        'Silver',
        'Turquoise',
        'Ultramarine',
        'Violet',
        'White',
        'Xanthic',
        'Yellow',
        'Zaffre',

        // Objects (A-Z)
        'Anchor',
        'Balloon',
        'Camera',
        'Diamond',
        'Eagle',
        'Feather',
        'Guitar',
        'Helmet',
        'Iceberg',
        'Jewel',
        'Kite',
        'Lighthouse',
        'Mountain',
        'Necklace',
        'Oxygen',
        'Piano',
        'Quill',
        'Rainbow',
        'Sailboat',
        'Treasure',
        'Umbrella',
        'Violin',
        'Waterfall',
        'Xylophone',
        'Yacht',
        'Zephyr',

        // Additional unique names
        'Alpha',
        'Beta',
        'Gamma',
        'Delta',
        'Epsilon',
        'Zeta',
        'Eta',
        'Theta',
        'Iota',
        'Kappa',
        'Lambda',
        'Mu',
        'Nu',
        'Xi',
        'Omicron',
        'Pi',
        'Rho',
        'Sigma',
        'Tau',
        'Upsilon',
        'Phi',
        'Chi',
        'Psi',
        'Omega',
        'Nebula',
        'Galaxy',
        'Comet',
        'Star',
        'Planet',
        'Moon',
    ];
    constructor() {}

    /**
     * Generate additional dummy name if we run out of predefined ones
     */
    private generateDummyName(index: number): string {
        const baseNames = ['Item', 'Field', 'Column', 'Data', 'Value'];
        const suffixes = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J'];

        const baseIndex = index % baseNames.length;
        const suffixIndex = Math.floor(index / baseNames.length) % suffixes.length;
        const number = Math.floor(index / (baseNames.length * suffixes.length)) + 1;

        return `${baseNames[baseIndex]}${suffixes[suffixIndex]}${number}`;
    }

    /**
     * Set column mappings for the current grid
     */
    setColumnMappings(columns: any[]): void {
        const mappings: ColumnMapping[] = columns
            .filter((col) => col.getColDef().headerName && col.getColId() !== 'ag-Grid-AutoColumn')
            .map((col, index) => ({
                originalField: col.getColId(),
                displayName: col.getColDef().headerName || col.getColId(),
                dummyName: index < this.dummyNames.length ? this.dummyNames[index] : this.generateDummyName(index - this.dummyNames.length),
                dataType: this.getDataTypeFromColDef(col.getColDef()),
            }));

        this.columnMappingsSubject.next(mappings);
    }

    getColumnMappings(): ColumnMapping[] {
        return this.columnMappingsSubject.value;
    }
 
    convertDummyQueryToReal(query: string): string {
        const mappings = this.getColumnMappings();
        let convertedQuery = query;

        mappings.forEach((mapping) => {
            const regex = new RegExp(`\\b${mapping.dummyName}\\b`, 'gi');
            convertedQuery = convertedQuery.replace(regex, mapping.originalField);
        });

        return convertedQuery;
    }

    applyQuery(query: string): void {
        const realQuery = this.convertDummyQueryToReal(query);
        this.appliedQuerySubject.next(realQuery);
        this.actionSubject.next({ action: 'applyQuery', query: realQuery });
    }
    clearQuery(): void {
        this.appliedQuerySubject.next(null);
        this.actionSubject.next({ action: 'clearQuery' });
    }

    setAppliedQuery(query: string): void {
        this.appliedQuerySubject.next(query);
    }

    getCurrentQuery(): string | null {
        return this.appliedQuerySubject.value;
    }

    getAvailableColumns(): string[] {
        return this.getColumnMappings().map((mapping) => mapping.displayName);
    }

    getDummyNameForDisplayName(displayName: string): string | null {
        const mapping = this.getColumnMappings().find((m) => m.displayName === displayName);
        return mapping ? mapping.dummyName : null;
    }

    getDisplayNameForDummyName(dummyName: string): string | null {
        const mapping = this.getColumnMappings().find((m) => m.dummyName === dummyName);
        return mapping ? mapping.displayName : null;
    }
    /**
     * Call OpenAI API to generate OData query automatically
     */
    generateQueryWithAI(userQuery: string): Observable<string> {
        this.loadingSubject.next(true);
        this.errorSubject.next(null);

        // const prompt = this.generateAIPrompt(userQuery);

        const body = {
            model: 'gpt-5',
            messages: [
                {
                    role: 'system',
                    content: 'You are an expert in OData queries. Generate only valid OData query strings without explanations.',
                },
                {
                    role: 'user',
                    content: prompt,
                },
            ],
        };

        return new Observable((observer) => {
            const xhr = new XMLHttpRequest();

            xhr.open('POST', this.OPENAI_API_URL, true);

            // Set headers directly to avoid Angular interceptors
            xhr.setRequestHeader('Authorization', `Bearer ${this.OPENAI_API_KEY}`);
            xhr.setRequestHeader('Content-Type', 'application/json');

            xhr.onreadystatechange = () => {
                if (xhr.readyState === 4) {
                    this.loadingSubject.next(false);

                    if (xhr.status === 200) {
                        try {
                            const response: OpenAIResponse = JSON.parse(xhr.responseText);
                            if (response.choices && response.choices.length > 0) {
                                const query = response.choices[0].message.content.trim();
                                observer.next(query);
                                observer.complete();
                            } else {
                                this.errorSubject.next('No response received from AI');
                                observer.error('No response received from AI');
                            }
                        } catch (parseError) {
                            console.error('Error parsing OpenAI response:', parseError);
                            this.errorSubject.next('Error parsing AI response');
                            observer.error(parseError);
                        }
                    } else {
                        console.error('OpenAI API Error:', xhr.status, xhr.statusText);
                        let errorMessage = 'Failed to generate query. Please check your API key and try again.';

                        try {
                            const errorResponse = JSON.parse(xhr.responseText);
                            if (errorResponse.error && errorResponse.error.message) {
                                errorMessage = errorResponse.error.message;
                            }
                        } catch (e) {
                            // Use default error message if parsing fails
                        }

                        this.errorSubject.next(errorMessage);
                        observer.error(new Error(errorMessage));
                    }
                }
            };

            xhr.onerror = () => {
                this.loadingSubject.next(false);
                console.error('Network error calling OpenAI API');
                this.errorSubject.next('Network error. Please check your internet connection.');
                observer.error(new Error('Network error'));
            };

            xhr.send(JSON.stringify(body));
        });
    }
    /**
     * Clear error state
     */
    clearError(): void {
        this.errorSubject.next(null);
    }

    private getDataTypeFromColDef(colDef: any): string {
        if (colDef.type) {
            return colDef.type;
        }

        // Try to determine type from valueGetter or field
        if (colDef.valueGetter || colDef.field) {
            // Default to string for most cases
            return 'string';
        }

        return 'string';
    }
}
